import type { TSDateYMDString } from 'src/types/datetime';

export function yearsSince(date: Date): number {
  const now = new Date();
  const years = now.getFullYear() - date.getFullYear();
  if (now.getMonth() < date.getMonth() || (now.getMonth() === date.getMonth() && now.getDate() < date.getDate())) {
    return years - 1;
  }
  return years;
}

// Local-calendar YYYY-MM-DD (not `.toISOString().slice(0, 10)`, which shifts
// across the day boundary by timezone). Downstream report resolvers are typed
// as ISO8601Date with no time component, so the date the user picks must equal
// the date the resolver receives regardless of timezone.
export function toIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Parse a date-only `YYYY-MM-DD` (the shape the BE returns and accepts via
// ISO8601Date) into a Date at local midnight. `new Date('2026-01-15')` would
// parse as UTC midnight and roll back a calendar day in negative-tz locales,
// selecting the wrong day in the picker — so build it from local parts.
export function parseLocalDate(iso: string | null): Date | undefined {
  if (!iso) return undefined;
  const [year, month, day] = iso.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day);
}

export function getStartOfToday() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

const EARLIEST_SUPPORTED_YEARS_BACK = 120;

// The floor for a historical date input (birth, death, trust formation). Pass it
// as the picker's `minDate`: react-day-picker's year dropdown otherwise stops at
// today minus 100 years, which already excludes living clients.
export function getEarliestSupportedDate() {
  const earliest = getStartOfToday();
  earliest.setFullYear(earliest.getFullYear() - EARLIEST_SUPPORTED_YEARS_BACK);
  return earliest;
}

export function createTSDate(date: string): TSDateYMDString | undefined {
  const datetime = !date ? new Date() : new Date(date);

  // Check if the date is valid
  if (isNaN(datetime.getTime())) return undefined;

  const year = datetime.getUTCFullYear();
  const month = datetime.getUTCMonth() + 1;
  const day = datetime.getUTCDate();

  // Basic validation
  if (year < 1900 || year > 2099) return undefined;
  if (month < 1 || month > 12) return undefined;
  if (day < 1 || day > 31) return undefined;

  const yearStr = year.toString();
  const monthStr = String(month).padStart(2, '0');
  const dayStr = String(day).padStart(2, '0');

  const dateStr = `${yearStr}-${monthStr}-${dayStr}`;

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return dateStr as TSDateYMDString;
}
